import { createFileRoute } from "@tanstack/react-router";
import { BASE_URL, SOCIAL_IMAGE_URL, abs } from "@/lib/seo/constants";
import { aeoGeo, breadcrumbs, faqPage } from "@/lib/seo/schema";
import { Button } from "@/components/site/Button";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/wow/Reveal";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { CareerForm } from "@/components/site/CareerForm";

const PATH = "/karriar";
const TITLE = "Karriär — filmteam och partnerfotografer | Fenny";
const DESCRIPTION =
  "Sök filmroller eller bli partnerfotograf hos Fenny. Vi bygger ett rikstäckande nätverk av fotografer som representerar Fenny ute hos kund.";
const CANONICAL = abs(PATH);
const DATE_MODIFIED = "2026-10-02";

export const Route = createFileRoute("/karriar")({
  head: () => ({
    title: TITLE,
    meta: [
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: SOCIAL_IMAGE_URL },
      { property: "og:locale", content: "sv_SE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: SOCIAL_IMAGE_URL },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(aeoGeo({
          path: PATH,
          name: "Karriär på Fenny",
          description: DESCRIPTION,
          question: "Söker Fenny nya medarbetare och partnerfotografer?",
          answer:
            "Ja — Fenny tar emot spontanansökningar inom film och söker Sverigebaserade partnerfotografer. Partnerfotografer representerar Fenny hos kund medan Fenny sköter försäljning, brief, projektledning, avtal och fakturering.",
          dateModified: DATE_MODIFIED,
          inLanguage: "sv-SE",
        })),
      },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Karriär", path: PATH }])) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
    ],
  }),
  component: KarriarPage,
});

function KarriarPage() {
  return (
    <main className="relative overflow-hidden">
      <GrainOverlay />

      {/* Hero */}
      <section className="relative flex min-h-[70vh] flex-col items-center justify-center px-6 py-24 text-center">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <p className="mb-4 text-sm font-medium uppercase tracking-widest text-mint">Karriär</p>
          </Reveal>
          <KineticHeading
            text="Vi söker alltid talanger — inte bara när en plats är öppen"
            as="h1"
            className="text-4xl font-semibold leading-[1.1] tracking-tight md:text-6xl"
            highlight={{ indices: [3, 4, 5], className: "text-mint" }}
          />
          <Reveal delay={0.35}>
            <p className="lede mx-auto mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl">
              Våra kunder söker proaktivitet, gränslös kreativitet och människor som tar initiativ. Om du är manusskribent, regissör, filmare, klippare, 3D-artist, casting-profil eller frilansare — vill vi lära känna dig.
            </p>
          </Reveal>
          <Reveal delay={0.55}>
            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button href="#ansok" variant="mint" arrow size="lg">
                Skicka ansökan
              </Button>
              <Button href="mailto:hey@fenny.se" variant="outline" size="lg">
                Mejla oss direkt
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Quick answer */}
      <Section>
        <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-surface/40 p-8 md:p-12">
          <p className="text-lg leading-relaxed" data-quick-answer>
            Fenny är en filmproduktionsbyrå i Stockholm som arbetar både med egna produktioner och kunduppdrag över hela Sverige. Vi tar emot spontanansökningar och frilansuppdrag löpande — oavsett om du vill jobba på plats, remote eller i projektform.
          </p>
        </div>
      </Section>

      {/* Roles */}
      <Section>
        <EyebrowHeading
          eyebrow="Vilka vi letar efter"
          title="Roller som alltid är intressanta"
          lede="Nedan finns de profiler vi ofta matchar mot våra kunders behov. Spontanansökan är alltid välkommen."
        />
        <RevealStagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" stagger={0.06}>
          {ROLES.map((r) => (
            <RevealItem key={r.label}>
              <div className="h-full rounded-2xl border border-white/10 bg-surface p-6 transition-colors hover:border-mint/30">
                <div className="mb-3 text-2xl">{r.icon}</div>
                <h3 className="text-lg font-semibold">{r.label}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </Section>

      {/* Partner photographers */}
      <Section className="bg-surface/30">
        <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-start">
          <EyebrowHeading
            eyebrow="Fenny Partnerfotograf"
            title="Fotografera. Vi sköter resten."
            lede="Vi bygger en fotoavdelning med Sverigebaserade partnerfotografer som agerar som Fenny ute hos kund. Varje fotograf behåller sin unika stil och sitt visuella uttryck — kunden väljer riktning och vi matchar rätt uppdrag med rätt fotograf."
          />
          <RevealStagger className="grid gap-4" stagger={0.06}>
            {PARTNER_PHOTOGRAPHER.map((item) => (
              <RevealItem key={item.label}>
                <div className="rounded-2xl border border-white/10 bg-background p-5">
                  <h3 className="font-semibold">{item.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
        <Reveal delay={0.2}>
          <div className="mt-8">
            <Button
              id="partnerfotograf-cta-sv"
              href="#ansok"
              variant="mint"
              arrow
              onClick={(event) => {
                event.preventDefault();
                window.dispatchEvent(new Event("fenny:select-partner-photographer"));
                window.history.replaceState(null, "", "#ansok");
                window.requestAnimationFrame(() => document.getElementById("ansok")?.scrollIntoView({ behavior: "smooth" }));
              }}
            >
              Ansök som partnerfotograf
            </Button>
          </div>
        </Reveal>
      </Section>

      {/* Culture */}
      <Section className="bg-surface/30">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          <EyebrowHeading
            eyebrow="Så är det att jobba med Fenny"
            title="Liten byrå, stort mandat"
            lede="Vi tror på snabba beslut, korta beslutsvägar och att varje person tar ägandeskap för sitt område."
          />
          <RevealStagger className="grid gap-4 sm:grid-cols-2" stagger={0.05}>
            {CULTURE.map((c) => (
              <RevealItem key={c.label}>
                <div className="rounded-2xl border border-white/10 bg-background p-5">
                  <h4 className="font-semibold">{c.label}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{c.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </Section>

      {/* Process */}
      <Section>
        <EyebrowHeading
          eyebrow="Processen"
          title="Från ansökan till samarbete"
          lede="Så här går det till när du hör av dig till oss."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-5">
          {PROCESS.map((step, i) => (
            <Reveal key={step.label} delay={i * 0.08}>
              <div className="relative rounded-2xl border border-white/10 bg-surface p-5 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-mint/15 text-sm font-semibold text-mint">
                  {i + 1}
                </div>
                <h4 className="font-semibold">{step.label}</h4>
                <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Form */}
      <CareerForm locale="sv" location="karriar" partnerCtaId="partnerfotograf-cta-sv" />

      {/* FAQ */}
      <Section className="bg-surface/30">
        <EyebrowHeading eyebrow="Vanliga frågor" title="Karriär & samarbete" />
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {FAQ.map((item) => (
            <Reveal key={item.q}>
              <div className="rounded-2xl border border-white/10 bg-background p-6">
                <h3 className="font-semibold">{item.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section>
        <div className="mx-auto max-w-3xl rounded-3xl border border-mint/20 bg-mint/5 p-10 text-center">
          <h2 className="text-2xl font-semibold md:text-3xl">Redo att visa vad du går för?</h2>
          <p className="mt-3 text-muted-foreground">
            Skicka en spontanansökan eller mejla hey@fenny.se — vi läser allt.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button href="#ansok" variant="mint" arrow size="lg">
              Ansök nu
            </Button>
            <Button href="mailto:hey@fenny.se" variant="outline" size="lg">
              hey@fenny.se
            </Button>
          </div>
        </div>
      </Section>
    </main>
  );
}

const ROLES = [
  { label: "Spontanansökan", icon: "✨", body: "Ingen perfekt roll på listan? Hör ändå av dig — vi sparar alla profiler." },
  { label: "Frilansare", icon: "🤝", body: "Jobba projektbaserat med oss och våra kunder, på distans eller på plats." },
  { label: "Fotograf (Partner)", icon: "📸", body: "Representera Fenny på projektbaserade fotouppdrag i din region." },
  { label: "Manusskribent", icon: "✍️", body: "Utveckla koncept, manus och berättelser för reklam, dokumentär och företagsfilm." },
  { label: "3D-artist", icon: "🧊", body: "Motion graphics, produktvisualisering och miljöer för både film och stillbild." },
  { label: "Klippare / editor", icon: "✂️", body: "Forma rytm, känsla och struktur i både korta och långa format." },
  { label: "Regissör", icon: "🎬", body: "Leda kreativa team och fånga rätt ton mellan varumärke och publik." },
  { label: "Casting / talangjakt", icon: "🎭", body: "Hitta rätt människor inför kamera — skådespelare, experter och vardagshjältar." },
  { label: "Post-produktion", icon: "🎛️", body: "Färg, ljud, VFX, retusch och leverans i alla kanaler." },
  { label: "Filmare / DOP", icon: "📷", body: "Fotografera och filma kommersiellt innehåll för webb, sociala medier och broadcast." },
];

const PARTNER_PHOTOGRAPHER = [
  {
    label: "Du fokuserar på fotograferingen",
    body: "Fenny sköter försäljning, kundkontakt, brief, projektledning, avtal och fakturering. Du kommer förberedd till uppdraget och levererar enligt överenskommen brief och kvalitetsnivå.",
  },
  {
    label: "Din stil styr matchningen",
    body: "Vi stöper inte fotografer i samma mall. Kunden väljer vilket visuellt uttryck den söker, och Fenny matchar uppdraget med den partnerfotograf vars personliga manér, nisch och estetik passar bäst.",
  },
  {
    label: "Du representerar Fenny",
    body: "Hos kunden agerar du som en del av Fenny, inte som en extern agenturprofil. Ett professionellt bemötande och ett tryggt genomförande är lika viktigt som bilderna.",
  },
  {
    label: "Uppdrag i hela Sverige",
    body: "Vi söker fotografer i Stockholm, Göteborg, Malmö och alla övriga regioner för att bygga ett rikstäckande nätverk nära våra kunder.",
  },
];

const CULTURE = [
  { label: "Proaktivitet belönas", body: "Vi uppskattar den som ser vad som behövs innan någon ber om det." },
  { label: "Korta beslutsvägar", body: "Inga långa hierarkier — ett samtal kan leda till ett uppdrag samma vecka." },
  { label: "Kreativ frihet", body: "Du får äga din del och föreslå lösningar, inte bara exekvera briefar." },
  { label: "Närhet till kunden", body: "Vi arbetar tätt med varumärken och lär oss deras världar på riktigt." },
];

const PROCESS = [
  { label: "Skicka ansökan", body: "Fyll i formuläret med roll, länk och ett kort meddelande." },
  { label: "Vi läser", body: "Varje ansökan granskas av produktionsteamet inom en vecka." },
  { label: "Kaffe-samtal", body: "Vid match bjuder vi in till ett kort, avslappnat samtal." },
  { label: "Testuppdrag", body: "Ett litet uppdrag visar hur vi samarbetar i praktiken." },
  { label: "Samarbete", body: "Vid ömsesidig fit blir du en del av vårt nätverk." },
];

const FAQ = [
  {
    q: "Hur fungerar uppdragen för en Fenny Partnerfotograf?",
    a: "Samarbetet är projektbaserat. Kunden väljer önskat visuellt uttryck och vi matchar uppdraget med den partnerfotograf vars personliga stil, region, erfarenhet och tillgänglighet passar bäst. Du tackar ja till det enskilda uppdraget och genomför fotograferingen enligt briefen.",
  },
  {
    q: "Behöver jag anpassa min stil till en Fenny-mall?",
    a: "Nej. Din personliga estetik är en viktig del av matchningen. Vi söker fotografer med olika manér — exempelvis dokumentärt, redaktionellt, avskalat eller varmt och livsstilsnära — så att kunder kan välja den stil som passar deras uppdrag.",
  },
  {
    q: "Vem representerar jag ute hos kunden?",
    a: "Du representerar Fenny och agerar som en del av vårt team under uppdraget. Fenny äger kundrelationen och sköter försäljning, kundkontakt, projektledning och avtal före, under och efter fotograferingen.",
  },
  {
    q: "Hur fungerar ersättning och fakturering?",
    a: "Du behöver kunna fakturera via eget bolag och ha F-skatt. Fenny fakturerar kunden och du fakturerar Fenny den ersättning vi har kommit överens om för uppdraget.",
  },
  {
    q: "Kan jag skicka en spontanansökan?",
    a: "Absolut — vi tar alltid emot spontanansökningar och sparar din profil för kommande uppdrag.",
  },
  {
    q: "Söker ni frilansare eller anställda?",
    a: "Båda. Många samarbeten börjar som frilansuppdrag och växer över tid.",
  },
  {
    q: "Var sitter Fenny?",
    a: "Vi har vårt huvudkontor i Stockholm men arbetar med kunder och talanger över hela Sverige.",
  },
  {
    q: "Vad ska jag skicka med?",
    a: "En länk till portfolio, showreel eller tidigare projekt — och ett kort personligt meddelande.",
  },
];
